import { organizations, users } from './auth-schema';
import { sql } from 'drizzle-orm';
import {
	boolean,
	check,
	foreignKey,
	index,
	integer,
	json,
	jsonb,
	numeric,
	pgEnum,
	pgPolicy,
	pgTable,
	primaryKey,
	text,
	timestamp,
	unique,
	uniqueIndex,
	uuid,
	varchar
} from 'drizzle-orm/pg-core';

export const entityType = pgEnum('entityType', [
	'session',
	'speaker',
	'sponsor',
	'attendee',
	'contentItem',
	'ticket',
	'registrationType',
	'addon',
	'sessionPackage'
]);
export const exportBatchStatus = pgEnum('export_batch_status', [
	'pending',
	'processing',
	'completed',
	'failed',
	'partially_completed'
]);
export const exportJobStatus = pgEnum('export_job_status', ['pending', 'processing', 'completed', 'failed', 'skipped']);
export const exportJobType = pgEnum('export_job_type', ['attendees', 'speakers', 'sponsors', 'sessions', 'orders']);

export const tickets = pgTable(
	'tickets',
	{
		ticketId: uuid('ticket_id').defaultRandom().primaryKey().notNull(),
		ticketTypeId: uuid('ticket_type_id').notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		name: text().notNull(),
		description: text(),
		price: numeric({ precision: 10, scale: 2 }),
		isPublished: boolean('is_published').default(true).notNull(),
		totalQuantity: integer('total_quantity'),
		displayOrder: integer('display_order'),
		availabilityStart: timestamp('availability_start', { mode: 'string' }),
		availabilityEnd: timestamp('availability_end', { mode: 'string' }),
		config: jsonb(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		refundPolicyId: uuid('refund_policy_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'tickets_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'tickets_refund_policies_refund_policy_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'tickets_organization_id_tenants_organization_id_fk'
		}),
		foreignKey({
			columns: [table.ticketTypeId],
			foreignColumns: [ticketTypes.ticketTypeId],
			name: 'tickets_ticket_type_id_fk'
		}),
		pgPolicy('tickets_require_scope', {
			as: 'permissive',
			for: 'select',
			to: ['public'],
			using: sql`(EXISTS ( SELECT 1
   FROM tickets t2
  WHERE ((t2.ticket_id = tickets.ticket_id) AND ((t2.organization_id = ANY ((regexp_split_to_array(current_setting('search.organization_ids'::text, true), ','::text))::uuid[])) OR (t2.event_id = ANY ((regexp_split_to_array(current_setting('search.event_ids'::text, true), ','::text))::uuid[]))))))`
		})
	]
);

export const refundPolicies = pgTable(
	'refund_policies',
	{
		refundPolicyId: uuid('refund_policy_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		isDefault: boolean('is_default').default(false)
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'refund_policies_event_id_fkey'
		}).onDelete('cascade')
	]
);

export const fieldTypes = pgTable('field_types', {
	fieldTypeId: uuid('field_type_id')
		.default(sql`gen_random_uuid()`)
		.primaryKey()
		.notNull(),
	key: text().notNull(),
	displayName: text('display_name').notNull(),
	description: text(),
	hasOptions: boolean('has_options').default(false),
	supportsValidation: boolean('supports_validation').default(false),
	supportsPlaceholder: boolean('supports_placeholder').default(true),
	supportsMultiple: boolean('supports_multiple').default(false),
	inputType: text('input_type'),
	createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
	updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
	isEnabled: boolean('is_enabled').default(true),
	parentFormFieldId: uuid('parent_form_field_id'),
	canBeChild: boolean('can_be_child').default(true),
	canBeParent: boolean('can_be_parent').default(true),
	allowedDependencyEvaluators: text('allowed_dependency_evaluators')
		.array()
		.default(sql`ARRAY['equals']::text[]`)
});

export const formFieldDependencies = pgTable(
	'form_field_dependencies',
	{
		formFieldDependencyId: uuid('form_field_dependency_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		formFieldId: uuid('form_field_id').notNull(),
		parentFieldId: uuid('parent_field_id').notNull(),
		conditionType: text('condition_type').notNull(),
		conditionValue: text('condition_value'),
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		conditionOptionIds: uuid('condition_option_ids')
			.array()
			.default(sql`ARRAY[]::uuid[]`)
	},
	(table) => [
		index('idx_form_field_dependencies_parent').using('btree', table.parentFieldId.asc().nullsLast().op('uuid_ops')),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_dependencies_form_field_id_fkey'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.parentFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_dependencies_parent_field_id_fkey'
		}).onDelete('cascade'),
		check(
			'form_field_dependencies_condition_type_check',
			sql`condition_type = ANY (ARRAY['equals'::text, 'not_equals'::text, 'contains'::text, 'starts_with'::text, 'ends_with'::text])`
		)
	]
);

export const formFieldOptions = pgTable(
	'form_field_options',
	{
		formFieldOptionId: uuid('form_field_option_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		formFieldId: uuid('form_field_id'),
		label: text().notNull(),
		value: text().notNull(),
		displayOrder: integer('display_order').default(0).notNull(),
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		deletedAt: timestamp('deleted_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_options_form_field_id_fkey'
		}).onDelete('cascade')
	]
);

export const formFields = pgTable(
	'form_fields',
	{
		formFieldId: uuid('form_field_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		label: text().notNull(),
		fieldTypeKey: text('field_type_key'),
		isSystem: boolean('is_system').default(false),
		isPreset: boolean('is_preset').default(false),
		displayOrder: integer('display_order').default(0).notNull(),
		colStart: integer('col_start').default(1).notNull(),
		colEnd: integer('col_end').default(13).notNull(),
		layoutOptions: jsonb('layout_options').default({}),
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		isRequired: boolean('is_required').default(false),
		description: text(),
		lookupEntity: text('lookup_entity'),
		placeholder: text(),
		name: text(),
		eventId: uuid('event_id'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		hiddenAt: timestamp('hidden_at', { mode: 'string' }),
		parentFormFieldId: uuid('parent_form_field_id') // Link to org-level parent question
	},
	(table) => [
		index('idx_form_fields_tenant').using('btree', table.organizationId.asc().nullsLast().op('uuid_ops')),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'form_fields_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.fieldTypeKey],
			foreignColumns: [fieldTypes.key],
			name: 'form_fields_field_type_key_fkey'
		}).onDelete('restrict'),
		foreignKey({
			columns: [table.parentFormFieldId],
			foreignColumns: [table.formFieldId],
			name: 'form_fields_parent_form_field_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'form_fields_organization_id_fkey'
		}).onDelete('cascade')
	]
);

export const formFieldVisibilityRules = pgTable(
	'form_field_visibility_rules',
	{
		visibilityRuleId: uuid('visibility_rule_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		formFieldId: uuid('form_field_id'),
		registrationTypeId: uuid('registration_type_id').notNull(),
		ticketId: uuid('ticket_id'),
		displayOrder: integer('display_order'),
		createAt: timestamp('create_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('form_field_visibility_rules_key_index').using(
			'btree',
			table.formFieldId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_visibility_rules_form_fields_form_field_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'form_field_visibility_rules_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'form_field_visibility_rules_tickets_ticket_id_fk'
		})
	]
);

export const formFieldValidationRules = pgTable(
	'form_field_validation_rules',
	{
		validationRuleId: uuid('validation_rule_id').defaultRandom().primaryKey().notNull(),
		formFieldId: uuid('form_field_id').notNull(),
		validationType: varchar('validation_type', { length: 50 }).notNull(), // e.g., 'min', 'max', 'phoneFormat', 'errorMessage'
		validationValue: text('validation_value'), // Stored as text, cast to appropriate type when needed
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_validation_rules_form_field_id_fk'
		}).onDelete('cascade')
	]
);

export const contentItems = pgTable(
	'content_items',
	{
		contentItemId: uuid('content_item_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		content: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'content_items_event_id_events_event_id_fk'
		})
	]
);

export const events = pgTable(
	'events',
	{
		eventId: uuid('event_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventTypeId: uuid('event_type_id').notNull(),
		slug: varchar({ length: 100 }),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		startDate: timestamp('start_date', { mode: 'string' }).notNull(),
		endDate: timestamp('end_date', { mode: 'string' }),
		url: varchar({ length: 255 }),
		venueId: uuid('venue_id'),
		isPublished: boolean('is_published').default(false).notNull(),
		isPrivate: boolean('is_private').default(false).notNull(),
		isInternal: boolean('is_internal').default(false).notNull(),
		theme: json(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		ownerEmail: varchar('owner_email'),
		timezone: varchar(),
		uniqueCode: text('unique_code').generatedAlwaysAs(
			sql`upper(encode(SUBSTRING(decode(md5((event_id)::text), 'hex'::text) FROM 1 FOR 4), 'hex'::text))`
		),
		confirmationPrefixCode: varchar('confirmation_prefix_code', { length: 16 }).unique()
	},
	(table) => [
		uniqueIndex('events_unique_code_uindex').using('btree', table.uniqueCode.asc().nullsLast().op('text_ops')),
		foreignKey({
			columns: [table.eventTypeId],
			foreignColumns: [eventTypes.eventTypeId],
			name: 'events_event_type_id_event_types_event_type_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'events_organization_id_tenants_organization_id_fk'
		}),
		foreignKey({
			columns: [table.venueId],
			foreignColumns: [venues.venueId],
			name: 'events_venue_id_venues_venue_id_fk'
		}),
		unique('events_slug_unique').on(table.slug)
	]
);

export const eventBranding = pgTable(
	'event_branding',
	{
		eventBrandingId: uuid('event_branding_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),

		// JSON column for flexible branding configuration
		brandingConfig: jsonb('branding_config').$type<{
			colors: {
				light: {
					primary: string;
					primaryForeground: string;
					secondary: string;
					secondaryForeground: string;
					background: string;
					foreground: string;
					destructive: string;
					destructiveForeground: string;
					info: string;
					infoForeground: string;
					layout: string;
					layoutForeground: string;
					muted?: string;
					mutedForeground?: string;
					accent?: string;
					accentForeground?: string;
					border?: string;
					input?: string;
					ring?: string;
					warning?: string;
					warningForeground?: string;
					success?: string;
					successForeground?: string;
				};
				dark?: {
					primary?: string;
					primaryForeground?: string;
					secondary?: string;
					secondaryForeground?: string;
					background?: string;
					foreground?: string;
					destructive?: string;
					destructiveForeground?: string;
					info?: string;
					infoForeground?: string;
					layout?: string;
					layoutForeground?: string;
					muted?: string;
					mutedForeground?: string;
					accent?: string;
					accentForeground?: string;
					border?: string;
					input?: string;
					ring?: string;
					warning?: string;
					warningForeground?: string;
					success?: string;
					successForeground?: string;
				};
			};
			typography?: {
				fontFamily?: {
					primary?: string;
					heading?: string;
					mono?: string;
				};
				fontWeight?: {
					normal?: number;
					medium?: number;
					semibold?: number;
					bold?: number;
				};
				customCss?: string;
			};
			interactions?: {
				animations?: {
					enabled?: boolean;
					duration?: 'fast' | 'normal' | 'slow';
				};
				feedback?: {
					haptic?: boolean;
					sound?: boolean;
				};
			};
			customCss?: string;
			// Image URLs stored temporarily in config (TODO: migrate to eventBrandingImages/eventBrandingImageAssignments)
			logoUrl?: string | null;
			heroImageUrl?: string | null;
			version?: string;
		}>(),

		// Metadata
		isActive: boolean('is_active').default(true).notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_branding_event_id_events_event_id_fk'
		}),
		unique('event_branding_event_id_unique').on(table.eventId)
	]
);

export const eventBrandingImages = pgTable(
	'event_branding_images',
	{
		imageId: uuid('image_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		organizationId: text('organization_id').notNull(),

		// File information
		url: varchar('url', { length: 500 }).notNull(),
		filename: varchar('filename', { length: 255 }).notNull(),
		mimeType: varchar('mime_type', { length: 100 }).notNull(),
		fileSize: integer('file_size'),
		width: integer('width'),
		height: integer('height'),
		altText: text('alt_text'),

		// Metadata
		uploadedBy: text('uploaded_by').notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_branding_images_event_id_events_event_id_fk'
		}),
		index('event_branding_images_event_id_idx').on(table.eventId)
	]
);

export const eventBrandingImageAssignments = pgTable(
	'event_branding_image_assignments',
	{
		assignmentId: uuid('assignment_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		imageId: uuid('image_id').notNull(),

		// Assignment configuration
		purpose: varchar('purpose', { length: 50 }).notNull(), // 'logo', 'hero', 'background', 'social_og', etc.
		colorMode: varchar('color_mode', { length: 10 }).default('all').notNull(), // 'light', 'dark', 'all'
		deviceType: varchar('device_type', { length: 20 }).default('all').notNull(), // 'desktop', 'mobile', 'tablet', 'all'

		// Metadata
		isActive: boolean('is_active').default(true).notNull(),
		sortOrder: integer('sort_order').default(0).notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_branding_image_assignments_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.imageId],
			foreignColumns: [eventBrandingImages.imageId],
			name: 'event_branding_image_assignments_image_id_event_branding_images_image_id_fk'
		}),
		unique('event_branding_image_assignments_unique').on(
			table.eventId,
			table.purpose,
			table.colorMode,
			table.deviceType
		),
		index('event_branding_image_assignments_event_id_idx').on(table.eventId),
		index('event_branding_image_assignments_image_id_idx').on(table.imageId)
	]
);

export const eventTypes = pgTable(
	'event_types',
	{
		eventTypeId: uuid('event_type_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		styles: jsonb(),
		lookupKey: varchar('lookup_key')
	},
	(table) => [unique('event_types_lookup_key_key').on(table.lookupKey)]
);

export const attendees = pgTable(
	'attendees',
	{
		attendeeId: uuid('attendee_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id'),
		firstName: varchar('first_name', { length: 255 }),
		lastName: varchar('last_name', { length: 255 }),
		displayName: varchar('display_name', { length: 255 }),
		profilePicture: varchar('profile_picture', { length: 255 }),
		title: varchar({ length: 255 }),
		company: varchar({ length: 255 }),
		bio: text(),
		extraProfileData: json('extra_profile_data'),
		isCheckedIn: boolean('is_checked_in').default(false).notNull(),
		registrationTypeId: uuid('registration_type_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		ticketId: uuid('ticket_id'),
		completedRegistrationAt: timestamp('completed_registration_at', { mode: 'string' }),
		activeRegistrationStatusId: uuid('active_registration_status_id'),
		checkedInAt: timestamp('checked_in_at', { mode: 'string' }),
		email: varchar('email', { length: 255 }),
		ownershipUserId: text('owner_ship_user_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendees_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'attendees_registration_type'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'attendees_tickets_fk'
		}),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'attendees_user_id_users_user_id_fk'
		}),
		// attendees_uk and attendees_email_uk are partial-unique indexes
		// (WHERE deleted_at IS NULL) — defined in migration 0053. Drizzle's
		// schema DSL doesn't model partial uniques, so they exist only in SQL.
		index('attendees_user_id_idx').on(table.userId),
		index('attendees_event_id_idx').on(table.eventId)
	]
);

export const orders = pgTable(
	'orders',
	{
		orderId: uuid('order_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),
		attendeeId: uuid('attendee_id'),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id'),
		// totalAmount = the order's gross at creation time (sum of orderItems
		// totalPrice for purchases). It is NEVER updated when items are
		// removed (orderItems.removedAt) — those refunds are tracked at the
		// payment level via payments.refundedAmount. To get "what is this
		// attendee's current liability for this order", compute:
		//   sum(orderItems WHERE removedAt IS NULL) - sum(payments.refundedAmount)
		// rather than reading totalAmount. Reports that sum totalAmount
		// across paid orders give "gross billed", which is usually NOT what
		// "revenue" or "amount paid" should report after refunds.
		totalAmount: numeric('total_amount', { precision: 10, scale: 2 }).notNull(),
		lifecycleState: varchar('lifecycle_state', { length: 20 }).default('ACTIVE').notNull(),
		paymentState: varchar('payment_state', { length: 20 }).default('UNPAID').notNull(),
		commitmentState: varchar('commitment_state', { length: 20 }).default('CONFIRMED').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		paidAt: timestamp('paid_at', { mode: 'string' }),
		paymentMethod: varchar('payment_method', { length: 100 }),
		paidBy: uuid('paid_by'),
		paymentToken: varchar('payment_token', { length: 64 }).unique().notNull(),
		pendingChanges: jsonb('pending_changes'),
		adminNote: text('admin_note'),
		smokeTestRunId: uuid('smoke_test_run_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'orders_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'orders_user_fk'
		}),
		index('orders_event_id_idx').on(table.eventId),
		index('orders_attendee_id_idx').on(table.attendeeId),
		index('orders_lifecycle_payment_idx').on(table.lifecycleState, table.paymentState),
		index('orders_smoke_test_run_id_idx').on(table.smokeTestRunId).where(sql`smoke_test_run_id IS NOT NULL`)
	]
);

export const attendeeRegistrationStatuses = pgTable(
	'attendee_registration_statuses',
	{
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		attendeeId: uuid('attendee_id'),
		status: varchar({ length: 100 }).notNull(),
		lastStep: uuid('last_step'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		emailValidationId: uuid('email_validation_id'),
		confirmationCode: varchar('confirmation_code', { length: 50 }),
		linkedRegistrationTypeId: uuid('linked_registration_type_id'),
		cancelledAt: timestamp('cancelled_at', { mode: 'string' }),
		cancelledBy: text('cancelled_by'),
		cancellationReason: text('cancellation_reason')
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'attendee_registration_statuses_attendees_attendee_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_registration_statuses_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.lastStep],
			foreignColumns: [registrationFormSteps.registrationFormStepId],
			name: 'attendee_registration_statuses_last_step_fk'
		}),
		foreignKey({
			columns: [table.linkedRegistrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'attendee_registration_statuses_linked_registration_type_id_fk'
		}),
		index('attendee_registration_statuses_event_id_idx').on(table.eventId),
		index('attendee_registration_statuses_attendee_id_idx').on(table.attendeeId)
	]
);

export const attendeeRegistrationEmailValidations = pgTable(
	'attendee_registration_email_validations',
	{
		attendeeRegistrationEmailValidationId: uuid('attendee_registration_email_validation_id')
			.defaultRandom()
			.primaryKey()
			.notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		eventId: uuid('event_id').notNull(),
		email: varchar('email', { length: 255 }).notNull(),
		token: varchar('token', { length: 255 }).notNull().unique(),
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),
		validatedAt: timestamp('validated_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'attendee_registration_email_validations_attendee_registration_statuses_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_registration_email_validations_events_event_id_fk'
		})
	]
);

export const attendeeChangeLogs = pgTable(
	'attendee_change_logs',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		eventId: uuid('event_id').notNull(),
		changeType: varchar('change_type', { length: 50 }).notNull(),
		previousState: jsonb('previous_state'),
		newState: jsonb('new_state'),
		changedByUserId: text('changed_by_user_id'),
		changedByUserType: varchar('changed_by_user_type', { length: 20 }).notNull(),
		changedAt: timestamp('changed_at', { mode: 'string' }).defaultNow().notNull(),
		notes: text('notes')
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'attendee_change_logs_attendee_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'attendee_change_logs_attendee_registration_status_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_change_logs_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.changedByUserId],
			foreignColumns: [users.id],
			name: 'attendee_change_logs_changed_by_user_id_fk'
		})
	]
);

export const registrationFormSteps = pgTable('registration_form_steps', {
	registrationFormStepId: uuid('registration_form_step_id').defaultRandom().primaryKey().notNull(),
	stepSlug: varchar('step_slug', { length: 100 }).notNull(),
	stepType: varchar('step_type', { length: 100 }).notNull(),
	title: varchar({ length: 500 }),
	description: text(),
	stepConfig: json('step_config'),
	ordinal: integer().default(99999).notNull(),
	createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
	createdBy: integer('created_by'),
	updatedAt: timestamp('updated_at', { mode: 'string' }),
	updatedBy: integer('updated_by'),
	deletedAt: timestamp('deleted_at', { mode: 'string' }),
	deletedBy: text('deleted_by'),
	reportingCode: varchar('reporting_code', { length: 255 }),
	eventId: uuid('event_id'),
	shortName: varchar('short_name'),
	triggerEmailUsageKey: varchar('trigger_email_usage_key'),
	icon: varchar('icon', { length: 255 })
});

export const attendeeRegistrationValues = pgTable(
	'attendee_registration_values',
	{
		attendeeRegistrationValueId: uuid('attendee_registration_value_id').defaultRandom().primaryKey().notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		value: jsonb(),
		fieldOptionId: uuid('field_option_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		eventId: uuid('event_id').notNull(),
		formFieldId: uuid('form_field_id'),
		fieldKey: varchar('field_key', { length: 200 })
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'attendee_registration_values_attendee_registration_statuses_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_registration_values_events_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'attendee_registration_values_form_fields_form_field_id_fk'
		})
	]
);

export const media = pgTable(
	'media',
	{
		mediaId: uuid('media_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		mediaType: varchar('media_type', { length: 100 }).notNull(),
		mediaUrl: varchar('media_url', { length: 255 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'media_event_id_events_event_id_fk'
		})
	]
);

export const payments = pgTable(
	'payments',
	{
		paymentId: uuid('payment_id').defaultRandom().primaryKey().notNull(),
		orderId: uuid('order_id'),
		paymentDate: timestamp('payment_date', { mode: 'string' }).defaultNow(),
		amount: numeric({ precision: 10, scale: 2 }).notNull(),
		paymentMethod: varchar('payment_method', { length: 100 }),
		paymentStatus: varchar('payment_status', { length: 100 }).default('PENDING'),
		transactionId: varchar('transaction_id', { length: 255 }),
		processorName: varchar('processor_name', { length: 100 }).notNull(),
		processorStatus: varchar('processor_status', { length: 50 }),
		responseData: json('response_data'),

		// Stripe-specific fields
		stripePaymentIntentId: varchar('stripe_payment_intent_id', { length: 255 }),
		stripeChargeId: varchar('stripe_charge_id', { length: 255 }),
		stripeFeeAmount: numeric('stripe_fee_amount', { precision: 10, scale: 2 }),
		sunfishFeeAmount: numeric('sunfish_fee_amount', { precision: 10, scale: 2 }),
		plannerNetAmount: numeric('planner_net_amount', { precision: 10, scale: 2 }),
		refundedAmount: numeric('refunded_amount', { precision: 10, scale: 2 }).default('0').notNull(),
		stripeRefundId: varchar('stripe_refund_id', { length: 255 }),
		refundReason: text('refund_reason'),

		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'payments_order_id_orders_order_id_fk'
		})
	]
);

export const processedWebhookEvents = pgTable(
	'processed_webhook_events',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		stripeEventId: varchar('stripe_event_id', { length: 255 }).notNull().unique(),
		eventType: varchar('event_type', { length: 100 }).notNull(),
		orderId: uuid('order_id'),
		paymentId: uuid('payment_id'),
		processedAt: timestamp('processed_at', { mode: 'string' }).defaultNow().notNull(),
		metadata: json('metadata')
	},
	(table) => [
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'processed_webhook_events_order_id_fk'
		})
	]
);

export const agendaSessions = pgTable(
	'agenda_sessions',
	{
		agendaSessionId: uuid('agenda_session_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		linkedAgendaSessionId: uuid('linked_session_id'),
		name: varchar({ length: 255 }).notNull(),
		teaser: varchar({ length: 255 }),
		description: text(),
		sessionType: varchar('session_type', { length: 100 }),
		startDate: timestamp('start_date', { mode: 'string' }).notNull(),
		endDate: timestamp('end_date', { mode: 'string' }).notNull(),
		capacity: integer(),
		locationId: uuid('location_id'),
		ticketId: uuid('ticket_id'),
		registrationRequired: boolean('registration_required').default(false),
		allowWaitlist: boolean('allow_waitlist').default(false),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'sessions_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.linkedAgendaSessionId],
			foreignColumns: [table.agendaSessionId],
			name: 'sessions_linked_session_id_sessions_session_id_fk'
		}),
		foreignKey({
			columns: [table.locationId],
			foreignColumns: [locations.locationId],
			name: 'sessions_location_id_locations_location_id_fk'
		})
	]
);

export const tags = pgTable(
	'tags',
	{
		tagId: uuid('tag_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 50 }).notNull(),
		description: varchar({ length: 1024 }),
		entityType: entityType('entity_type'),
		color: varchar({ length: 50 }),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'tags_event_id_events_event_id_fk'
		})
	]
);

export const speakerTypes = pgTable(
	'speaker_types',
	{
		speakerTypeId: uuid('speaker_type_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 50 }).notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'speaker_types_event_id_events_event_id_fk'
		})
	]
);

export const sessionSpeakers = pgTable(
	'session_speakers',
	{
		sessionSpeakerId: uuid('session_speaker_id').defaultRandom().primaryKey().notNull(),
		agendaSessionId: uuid('agenda_session_id').notNull(),
		firstName: varchar('first_name', { length: 255 }),
		lastName: varchar('last_name', { length: 255 }),
		jobTitle: varchar('job_title', { length: 255 }),
		company: varchar({ length: 255 }),
		attendeeId: uuid('attendee_id').notNull(),
		speakerTypeId: uuid('speaker_type_id'),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'speaker_attendee_fk'
		}),
		foreignKey({
			columns: [table.speakerTypeId],
			foreignColumns: [speakerTypes.speakerTypeId],
			name: 'speaker_speaker_type_fk'
		})
	]
);

export const venues = pgTable(
	'venues',
	{
		venueId: uuid('venue_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		type: varchar({ length: 100 }).default('physical').notNull(),
		name: varchar({ length: 255 }).notNull(),
		address: text(),
		city: varchar({ length: 100 }),
		state: varchar({ length: 100 }),
		country: varchar({ length: 100 }),
		timezone: varchar({ length: 50 }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		placeId: varchar('place_id', { length: 500 })
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'venues_organization_id_tenants_organization_id_fk'
		})
	]
);

export const locations = pgTable(
	'locations',
	{
		locationId: uuid('location_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'locations_organization_id_tenants_organization_id_fk'
		})
	]
);

export const ticketTypes = pgTable(
	'ticket_types',
	{
		ticketTypeId: uuid('ticket_type_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id'),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		lookupKey: varchar('lookup_key')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'ticket_types_organization_id_tenants_organization_id_fk'
		})
	]
);

export const orderItems = pgTable(
	'order_items',
	{
		orderItemId: uuid('order_item_id').defaultRandom().primaryKey().notNull(),
		orderId: uuid('order_id').notNull(),
		entityType: entityType('entity_type'),
		entityId: uuid('entity_id'),
		entityLabel: varchar('entity_label', { length: 255 }),
		pricePerItem: numeric('price_per_item', { precision: 10, scale: 2 }).notNull(),
		totalPrice: numeric('total_price', { precision: 10, scale: 2 }).notNull(),
		quantity: integer().default(1).notNull(),
		lineItemType: varchar('line_item_type', { length: 20 }).default('purchase').notNull(),
		registrationTypeId: uuid('registration_type_id'),
		// On credit rows (lineItemType='credit'), points to the order containing
		// the original purchase being netted out. Null on purchase/waiver rows.
		// Lets the drain-fee math at refund time group credits by their source
		// order without entityId-lookup ambiguity (an entity can have purchases
		// across multiple orders after re-buy chains).
		sourceOrderId: uuid('source_order_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		removedAt: timestamp('removed_at', { mode: 'string' }),
		removedBy: text('removed_by'),
		discountAllocation: jsonb('discount_allocation')
	},
	(table) => [
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'order_items_order_id_orders_order_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.sourceOrderId],
			foreignColumns: [orders.orderId],
			name: 'order_items_source_order_id_orders_order_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'order_items_registration_type_id_fk'
		}),
		// order_items_uk is a partial-unique index (WHERE removed_at IS NULL)
		// defined in migration 0053. Drizzle's schema DSL doesn't model partial
		// uniques. The active-only constraint lets a customer re-purchase an
		// entity_id after the original was removed (orderItems.removedAt set).

	]
);

export const refundRules = pgTable(
	'refund_rules',
	{
		refundRuleId: uuid('refund_rule_id').defaultRandom().primaryKey().notNull(),
		refundPolicyId: uuid('refund_policy_id').notNull(),
		startDate: timestamp('start_date', { mode: 'string' }).notNull(),
		endDate: timestamp('end_date', { mode: 'string' }).notNull(),
		refundType: varchar('refund_type', { length: 50 }).notNull(),
		refundValue: numeric('refund_value', { precision: 10, scale: 2 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'refund_rules_refund_policy_id_fkey'
		}).onDelete('cascade'),
		check(
			'refund_rules_refund_type_check',
			sql`(refund_type)::text = ANY (ARRAY[('percentage'::character varying)::text, ('flat'::character varying)::text])`
		),
		check('refund_rules_refund_value_check', sql`refund_value >= (0)::numeric`)
	]
);

export const registrationTypes = pgTable(
	'registration_types',
	{
		registrationTypeId: uuid('registration_type_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 50 }).notNull(),
		organizationId: text('organization_id').notNull(),
		description: text(),
		isShareable: boolean('is_shareable').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		reportingCode: varchar('reporting_code', { length: 255 }),
		eventId: uuid('event_id'),
		createdBy: text('created_by'),
		updatedBy: text('updated_by'),
		deletedBy: text('deleted_by'),
		tenantLevelId: uuid('tenant_level_id'),
		isHidden: boolean('is_hidden').default(false).notNull(),
		isDefault: boolean('is_default').default(false).notNull(),
		uniqueCode: text('unique_code').generatedAlwaysAs(
			sql`upper(encode(SUBSTRING(decode(md5((registration_type_id)::text), 'hex'::text) FROM 1 FOR 4), 'hex'::text))`
		)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'registration_type_tenant_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_types_events_event_id_fk'
		}),
		uniqueIndex('registration_types_unique_code_uindex').using(
			'btree',
			table.uniqueCode.asc().nullsLast().op('text_ops')
		)
	]
);

export const addons = pgTable(
	'addons',
	{
		addonId: uuid('addon_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		addonTypeId: uuid('addon_type_id'),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		details: json(),
		price: numeric({ precision: 10, scale: 2 }).notNull(),
		isDefault: boolean('is_default').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		totalQuantity: integer('total_quantity'),
		availabilityStart: timestamp('availability_start', { mode: 'string' }),
		availabilityEnd: timestamp('availability_end', { mode: 'string' }),
		refundPolicyId: uuid('refund_policy_id'),
		isPublished: boolean('is_published').default(true),
		displayOrder: integer('display_order').default(0)
	},
	(table) => [
		foreignKey({
			columns: [table.addonTypeId],
			foreignColumns: [addonTypes.addonTypeId],
			name: 'addons_addon_type_id_addon_types_addon_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'addons_event_id'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'addons_refund_policies_refund_policy_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'addons_organization_id'
		})
	]
);

export const addonTypes = pgTable(
	'addon_types',
	{
		addonTypeId: uuid('addon_type_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		details: json(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'addon_types_tenants'
		})
	]
);

export const sessionPackages = pgTable(
	'session_packages',
	{
		sessionPackageId: uuid('session_package_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		price: numeric({ precision: 10, scale: 2 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		totalQuantity: integer('total_quantity'),
		availabilityStart: timestamp('availability_start', { mode: 'string' }),
		availabilityEnd: timestamp('availability_end', { mode: 'string' }),
		refundPolicyId: uuid('refund_policy_id'),
		isPublished: boolean('is_published').default(true)
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'session_packages_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'session_packages_refund_policies_refund_policy_id_fk'
		})
	]
);

export const registrationMessages = pgTable(
	'registration_messages',
	{
		registrationMessageId: uuid('registration_message_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		messageType: varchar('message_type', { length: 100 }).default('COMPLETION'),
		messageContent: text('message_content').notNull(),
		name: text().notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		isDefault: boolean('is_default').default(false).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_messages_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'registration_messages_organization_id_fk'
		}).onDelete('cascade')
	]
);

export const registrationMessageAssociations = pgTable(
	'registration_message_associations',
	{
		registrationMessagesAssociationId: uuid('registration_messages_association_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		registrationMessageId: uuid('registration_message_id'),
		registrationTypeId: uuid('registration_type_id'),
		ticketId: uuid('ticket_id'),
		createAt: timestamp('create_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('registration_message_associations_key_index').using(
			'btree',
			table.registrationMessageId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.registrationMessageId],
			foreignColumns: [registrationMessages.registrationMessageId],
			name: 'registration_message_associations_registration_message_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_message_associations_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'registration_message_associations_tickets_ticket_id_fk'
		})
	]
);

export const registrationTypeOverrides = pgTable(
	'registration_type_overrides',
	{
		registrationTypeOverrideId: uuid('registration_type_override_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		entityId: uuid('entity_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		price: numeric({ precision: 10, scale: 2 }),
		startDate: timestamp('start_date', { mode: 'string' }),
		endDate: timestamp('end_date', { mode: 'string' }),
		overrideDetails: json('override_details'),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		refundPolicyId: uuid('refund_policy_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_type_overrides_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'registration_type_overrides_refund_policies_refund_policy_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_type_overrides_registration_type_fk'
		})
	]
);

export const sessionContentItems = pgTable(
	'session_content_items',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		contentId: uuid('content_id').notNull(),
		section: varchar({ length: 100 }),
		ordinal: integer().default(99999).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.contentId],
			foreignColumns: [contentItems.contentItemId],
			name: 'session_content_item_content_fk'
		}),
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_content_item_session_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.contentId],
			name: 'session_content_items_session_id_content_id_pk'
		})
	]
);

export const defaultEventSettings = pgTable(
	'default_event_settings',
	{
		settingKey: text('setting_key').notNull(),
		settingValue: jsonb('setting_value').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		updatedAt: timestamp('updated_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		organizationId: text('organization_id')
			.default(sql`'00000000-0000-0000-0000-000000000000'`)
			.notNull()
	},
	(table) => [
		index('default_event_settings_organization_id_setting_key_index').using(
			'btree',
			table.settingKey.asc().nullsLast().op('text_ops'),
			table.organizationId.asc().nullsLast().op('text_ops')
		),
		primaryKey({ columns: [table.settingKey, table.organizationId], name: 'default_event_settings_pk' })
	]
);

export const sessionsMedia = pgTable(
	'sessions_media',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		mediaId: uuid('media_id').notNull(),
		ordinal: integer().default(99999).notNull(),
		isHero: boolean('is_hero').default(false),
		isThumbnail: boolean('is_thumbnail').default(false),
		isDownloadable: boolean('is_downloadable').default(false)
	},
	(table) => [
		foreignKey({
			columns: [table.mediaId],
			foreignColumns: [media.mediaId],
			name: 'sessions_media_media_id_media_media_id_fk'
		}),
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'sessions_media_session_id_sessions_session_id_fk'
		}),
		primaryKey({ columns: [table.agendaSessionId, table.mediaId], name: 'sessions_media_session_id_media_id_pk' })
	]
);

export const eventSettings = pgTable(
	'event_settings',
	{
		eventId: uuid('event_id').notNull(),
		settingKey: text('setting_key').notNull(),
		settingValue: jsonb('setting_value').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'events_settings_event_id_events_event_id_fk'
		}),
		primaryKey({ columns: [table.eventId, table.settingKey], name: 'event_settings_pkey' })
	]
);

export const sessionPackagesSessions = pgTable(
	'session_packages_sessions',
	{
		sessionPackageId: uuid('session_package_id').notNull(),
		agendaSessionId: uuid('agenda_session_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_packages_sessions_session_fk'
		}),
		foreignKey({
			columns: [table.sessionPackageId],
			foreignColumns: [sessionPackages.sessionPackageId],
			name: 'session_packages_sessions_session_package_fk'
		}),
		primaryKey({
			columns: [table.sessionPackageId, table.agendaSessionId],
			name: 'session_packages_sessions_session_package_id_session_id_pk'
		})
	]
);

export const registrationTypeItems = pgTable(
	'registration_type_items',
	{
		eventId: uuid('event_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		entityId: uuid('entity_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_type_items_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_type_items_registration_type_fk'
		}),
		primaryKey({
			columns: [table.eventId, table.registrationTypeId, table.entityId, table.entityType],
			name: 'registration_type_items_pk'
		})
	]
);

export const discounts = pgTable(
	'discounts',
	{
		discountId: uuid('discount_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		displayName: text('display_name').notNull(),
		description: text(),
		codeBehavior: varchar('code_behavior', { length: 30 }).default('discount').notNull(),
		discountType: varchar('discount_type', { length: 20 }),
		discountValue: numeric('discount_value', { precision: 10, scale: 4 }),
		currency: varchar({ length: 3 }).default('usd').notNull(),
		maxTotalUses: integer('max_total_uses'),
		maxUsesPerEmail: integer('max_uses_per_email'),
		maxUsesPerUser: integer('max_uses_per_user'),
		maxQuantityPerOrder: integer('max_quantity_per_order'),
		minOrderAmount: numeric('min_order_amount', { precision: 10, scale: 2 }),
		validFrom: timestamp('valid_from', { mode: 'string' }),
		validTo: timestamp('valid_to', { mode: 'string' }),
		stackable: boolean().default(false).notNull(),
		usageCount: integer('usage_count').default(0).notNull(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'discounts_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'discounts_event_id_fk'
		}).onDelete('cascade'),
		index('discounts_organization_id_idx').on(table.organizationId),
		index('discounts_event_id_idx').on(table.eventId),
		pgPolicy('discounts_require_scope', {
			as: 'permissive',
			for: 'select',
			to: ['public'],
			using: sql`((organization_id = ANY ((regexp_split_to_array(current_setting('search.organization_ids'::text, true), ','::text))::text[])) OR (event_id = ANY ((regexp_split_to_array(current_setting('search.event_ids'::text, true), ','::text))::uuid[]))) `
		})
	]
);

export const discountCodes = pgTable(
	'discount_codes',
	{
		discountCodeId: uuid('discount_code_id').defaultRandom().primaryKey().notNull(),
		discountId: uuid('discount_id').notNull(),
		code: varchar({ length: 64 }).notNull(),
		codeNormalized: text('code_normalized').generatedAlwaysAs(sql`upper(code)`),
		maxTotalUses: integer('max_total_uses'),
		usageCount: integer('usage_count').default(0).notNull(),
		isActive: boolean('is_active').default(true).notNull(),
		generatedInBatchId: uuid('generated_in_batch_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'discount_codes_discount_id_fk'
		}).onDelete('cascade'),
		index('discount_codes_discount_id_idx').on(table.discountId),
		index('discount_codes_code_normalized_idx').on(table.codeNormalized)
	]
);

export const discountCodeRedemptions = pgTable(
	'discount_code_redemptions',
	{
		discountCodeRedemptionId: uuid('discount_code_redemption_id').defaultRandom().primaryKey().notNull(),
		discountId: uuid('discount_id').notNull(),
		discountCodeId: uuid('discount_code_id').notNull(),
		orderId: uuid('order_id').notNull(),
		orderItemId: uuid('order_item_id'),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		userId: text('user_id'),
		email: varchar({ length: 255 }).notNull(),
		discountAmount: numeric('discount_amount', { precision: 10, scale: 2 }).notNull(),
		currency: varchar({ length: 3 }).notNull(),
		redeemedAt: timestamp('redeemed_at', { mode: 'string' }).defaultNow().notNull(),
		confirmedAt: timestamp('confirmed_at', { mode: 'string' }),
		voidedAt: timestamp('voided_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'discount_code_redemptions_discount_id_fk'
		}),
		foreignKey({
			columns: [table.discountCodeId],
			foreignColumns: [discountCodes.discountCodeId],
			name: 'discount_code_redemptions_discount_code_id_fk'
		}),
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'discount_code_redemptions_order_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'discount_code_redemptions_status_id_fk'
		})
	]
);

export const discountApplicability = pgTable(
	'discount_applicability',
	{
		discountId: uuid('discount_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		entityId: uuid('entity_id').notNull(),
		mode: varchar({ length: 10 }).default('include').notNull()
	},
	(table) => [
		primaryKey({ columns: [table.discountId, table.entityType, table.entityId] }),
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'discount_applicability_discount_id_fk'
		}).onDelete('cascade')
	]
);

export const eventDiscounts = pgTable(
	'event_discounts',
	{
		eventId: uuid('event_id').notNull(),
		discountId: uuid('discount_id').notNull(),
		publishedAt: timestamp('published_at', { mode: 'string' }).defaultNow().notNull(),
		publishedBy: text('published_by')
	},
	(table) => [
		primaryKey({ columns: [table.eventId, table.discountId] }),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_discounts_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'event_discounts_discount_id_fk'
		}).onDelete('cascade')
	]
);

export const sessionTags = pgTable(
	'session_tags',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		tagId: uuid('tag_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_tag_session_fk'
		}),
		foreignKey({
			columns: [table.tagId],
			foreignColumns: [contentItems.contentItemId],
			name: 'session_tag_tag_fk'
		}),
		primaryKey({ columns: [table.agendaSessionId, table.tagId], name: 'session_tags_session_id_tag_id_pk' })
	]
);

export const sessionRegistrations = pgTable(
	'session_registrations',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		hasAttended: boolean('has_attended').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_registration_session_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.attendeeId],
			name: 'session_registrations_session_id_attendee_id_pk'
		})
	]
);

export const sessionWaitlist = pgTable(
	'session_waitlist',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		sessionRegistrationId: uuid('session_registration_id'),
		invitedToRegister: boolean('invited_to_register').default(false).notNull(),
		registrationExpires: timestamp('registration_expires', { mode: 'string' }),
		isMovedToRegistration: boolean('is_moved_to_registration').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'session_waitlist_attendee_fk'
		}),
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_waitlist_session_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.attendeeId],
			name: 'session_waitlist_session_id_attendee_id_pk'
		})
	]
);

export const emailTemplates = pgTable(
	'email_templates',
	{
		emailTemplateId: uuid('email_template_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		usageKey: varchar('usage_key', { length: 100 }).notNull(),
		name: text().notNull(),
		subject: text().notNull(),
		body: text().notNull(),
		config: jsonb('config').$type<{
			button?: { text?: string; href?: string };
			buttons?: Array<{ text: string; href: string }>;
			headerImage?: string;
			hideOrderSummaryForFree?: boolean;
		}>(),
		fromName: text('from_name'),
		replyToEmail: text('reply_to_email'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		isDefault: boolean('is_default').default(false).notNull(),
		isPaused: boolean('is_paused').default(false).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'email_templates_event_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'email_templates_organization_id_fk'
		})
	]
);
export const emailTemplateAssociations = pgTable(
	'email_template_associations',
	{
		emailTemplateAssociationId: uuid('email_template_association_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		emailTemplateId: uuid('email_template_id'),
		registrationTypeId: uuid('registration_type_id'),
		ticketId: uuid('ticket_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('email_template_associations_key_index').using(
			'btree',
			table.emailTemplateId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.emailTemplateId],
			foreignColumns: [emailTemplates.emailTemplateId],
			name: 'email_template_associations_registration_message_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'email_template_associations_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'email_template_associations_tickets_ticket_id_fk'
		}),
		pgPolicy('email_template_associations select policy', { as: 'permissive', for: 'select', to: ['public'] }),
		pgPolicy('email_template_associations update policy', { as: 'permissive', for: 'update', to: ['public'] }),
		pgPolicy('email_template_associations delete policy', { as: 'permissive', for: 'delete', to: ['public'] }),
		pgPolicy('email_template_associations insert policy', { as: 'permissive', for: 'insert', to: ['public'] })
	]
);

export const emailEventLogs = pgTable(
	'email_event_logs',
	{
		emailEventLogId: uuid('email_event_log_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id'),
		emailTemplateId: uuid('email_template_id'),
		from: text('from').notNull(),
		subject: text('subject').notNull(),
		recipientEmail: text('recipient_email').notNull(),
		recipientAttendeeId: uuid('recipient_attendee_id'),
		recipientUserId: text('recipient_user_id'),
		orderId: uuid('order_id'),
		triggeredBy: text('triggered_by').notNull(),
		isTestSend: boolean('is_test_send').default(false).notNull(),
		eventType: varchar('event_type', { length: 100 }).notNull(),
		emailId: text('email_id'),
		data: jsonb('data'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'email_event_log_event_id_fk'
		}),
		foreignKey({
			columns: [table.emailTemplateId],
			foreignColumns: [emailTemplates.emailTemplateId],
			name: 'email_event_log_email_template_id_fk'
		})
	]
);

export const emailVariableDefinitions = pgTable(
	'email_variable_definitions',
	{
		emailVariableDefinitionId: uuid('email_variable_definition_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		usageKey: varchar('usage_key', { length: 100 }).notNull(),
		key: varchar('key', { length: 100 }).notNull(),
		display: text('display').notNull(),
		allowedFields: text('allowed_fields').array(),
		valueSource: varchar('value_source', { length: 20 }).notNull().default('computed'),
		valuePath: text('value_path'),
		valueTransform: varchar('value_transform', { length: 50 }),
		displayOrder: integer('display_order').default(0).notNull(),
		isActive: boolean('is_active').default(true).notNull(),
		sampleValue: text('sample_value'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'email_variable_definitions_event_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'email_variable_definitions_organization_id_fk'
		}),
		unique('email_variable_definitions_scope_unique')
			.on(table.organizationId, table.eventId, table.usageKey, table.key)
			.nullsNotDistinct(),
		index('idx_email_var_defs_usage_key').using('btree', table.usageKey),
		index('idx_email_var_defs_event_id').using('btree', table.eventId)
	]
);

export const featureFlags = pgTable(
	'feature_flags',
	{
		featureFlagId: uuid('feature_flag_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 100 }).notNull(),
		description: text(),
		isDefaultEnabled: boolean('is_default_enabled').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		unique('feature_flags_name_key').on(table.name),
		unique('feature_flags_feature_flag_id_key').on(table.featureFlagId)
	]
);

export const organizationFeatureFlags = pgTable(
	'organization_feature_flags',
	{
		organizationFeatureFlagId: uuid('organization_feature_flag_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		featureFlagId: uuid('feature_flag_id').notNull(),
		isEnabled: boolean('is_enabled').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'organization_feature_flags_organization_id_fk'
		}),
		foreignKey({
			columns: [table.featureFlagId],
			foreignColumns: [featureFlags.featureFlagId],
			name: 'organization_feature_flags_feature_flag_id_fk'
		}),
		unique('organization_feature_flags_organization_id_feature_flag_id_key').on(
			table.organizationId,
			table.featureFlagId
		)
	]
);

export const paymentProviderEnum = pgEnum('payment_provider', ['stripe', 'square', 'paypal']);

export const organizationPaymentConfig = pgTable(
	'organization_payment_config',
	{
		organizationId: text('organization_id').primaryKey().notNull(),

		// Payment provider
		provider: paymentProviderEnum().default('stripe').notNull(),
		providerAccountId: varchar('provider_account_id', { length: 255 }),
		providerOnboardingComplete: boolean('provider_onboarding_complete').default(false).notNull(),
		testMode: boolean('test_mode').default(false).notNull(),

		// Provider-specific configuration (varies by provider)
		providerConfig: jsonb('provider_config').$type<{
			// Stripe-specific
			stripeAccountType?: 'express' | 'standard' | 'custom';
			stripeCapabilities?: string[];
			// Square-specific
			squareLocationId?: string;
			// PayPal-specific
			paypalMerchantId?: string;
			// Generic fields for future providers
			[key: string]: unknown;
		}>(),

		// Sunfish fee configuration (platform-level, applies to all providers)
		sunfishFeeEnabled: boolean('sunfish_fee_enabled').default(true).notNull(),
		sunfishFeePercent: numeric('sunfish_fee_percent', { precision: 5, scale: 4 }).default('0.01').notNull(),
		sunfishFeeFixed: numeric('sunfish_fee_fixed', { precision: 10, scale: 2 }).default('0.99').notNull(),
		sunfishFeeCap: numeric('sunfish_fee_cap', { precision: 10, scale: 2 }).default('4.99').notNull(),

		// Allowed payment methods (superset for all events under this org)
		// null = automatic (Stripe decides). Array = explicit list e.g. ['card', 'cashapp', 'us_bank_account']
		allowedPaymentMethods: jsonb('allowed_payment_methods').$type<string[] | null>(),

		// Currency
		defaultCurrency: varchar('default_currency', { length: 3 }).default('usd').notNull(),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'organization_payment_config_organization_id_fk'
		})
	]
);

export const adminLinks = pgTable(
	'admin_links',
	{
		adminLinkId: uuid('admin_link_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 255 }).notNull(),
		url: text('url').notNull(),
		icon: text('icon'),
		ordinal: integer('ordinal').default(9999).notNull(),
		usage: varchar({ length: 100 }),
		parentLinkId: uuid('parent_link_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.parentLinkId],
			foreignColumns: [table.adminLinkId],
			name: 'admin_links_parent_link_id_fk'
		})
	]
);

export const adminLinkConditions = pgTable(
	'admin_link_conditions',
	{
		adminLinkConditionId: uuid('admin_link_condition_id').defaultRandom().primaryKey().notNull(),
		adminLinkId: uuid('admin_link_id').notNull(),
		conditionType: varchar('condition_type', { length: 50 }).notNull(),
		conditionField: varchar('condition_field', { length: 100 }).notNull(),
		conditionOperation: varchar('condition_operation', { length: 50 }).notNull(),
		conditionValue: text('condition_value'),
		combination: text('combination').default('AND').notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.adminLinkId],
			foreignColumns: [adminLinks.adminLinkId],
			name: 'admin_link_conditions_admin_link_id_fk'
		})
	]
);

// Landing Pages Schema
export const landingPages = pgTable(
	'landing_pages',
	{
		landingPageId: uuid('landing_page_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		title: text().notNull(),
		urlSlug: varchar('url_slug', { length: 255 }).notNull(),
		settings: jsonb().$type<{
			heroCardSettings?: {
				showEventType?: boolean;
				showDateAndTime?: boolean;
				showVenue?: boolean;
				showRegisterButton?: boolean;
				registerButtonText?: string;
			};
			headerSettings?: {
				showLogo?: boolean;
				showEventType?: boolean;
				showLoginLink?: boolean;
				loginLinkText?: string;
				showRegisterButton?: boolean;
				registerButtonText?: string;
			};
		}>(),
		publishedAt: timestamp('published_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'landing_pages_event_id_fk'
		}).onDelete('cascade'),
		unique('landing_pages_url_slug_key').on(table.urlSlug),
		index('landing_pages_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('landing_pages_published_at_idx').using('btree', table.publishedAt.asc().nullsLast())
	]
);

export const landingPagePreviewTokens = pgTable(
	'landing_page_preview_tokens',
	{
		tokenId: uuid('token_id').defaultRandom().primaryKey().notNull(),
		landingPageId: uuid('landing_page_id').notNull(),
		token: varchar('token', { length: 64 }).notNull().unique(),
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		createdBy: text('created_by'),
		usedAt: timestamp('used_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.landingPageId],
			foreignColumns: [landingPages.landingPageId],
			name: 'landing_page_preview_tokens_landing_page_id_fk'
		}).onDelete('cascade'),
		index('landing_page_preview_tokens_token_idx').using('btree', table.token.asc()),
		index('landing_page_preview_tokens_expires_at_idx').using('btree', table.expiresAt.asc())
	]
);

export const landingPageBlocks = pgTable(
	'landing_page_blocks',
	{
		landingPageBlockId: uuid('landing_page_block_id').defaultRandom().primaryKey().notNull(),
		landingPageId: uuid('landing_page_id').notNull(),
		blockType: varchar('block_type', { length: 100 }).notNull(),
		content: jsonb().$type<{
			name?: string;
			description?: string;
			imageUrl?: string;
			buttonText?: string;
			buttonUrl?: string;
			showRegistrationButton?: boolean;
			showLoginToReviewButton?: boolean;
			[key: string]: any;
		}>(),
		displayOrder: integer('display_order').default(0).notNull(),
		isVisible: boolean('is_visible').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.landingPageId],
			foreignColumns: [landingPages.landingPageId],
			name: 'landing_page_blocks_landing_page_id_fk'
		}).onDelete('cascade'),
		index('landing_page_blocks_landing_page_id_idx').using('btree', table.landingPageId.asc().nullsLast()),
		index('landing_page_blocks_display_order_idx').using('btree', table.displayOrder.asc().nullsLast())
	]
);

// Analytics Data Cache
export const analyticsData = pgTable(
	'analytics_data',
	{
		analyticsDataId: uuid('analytics_data_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		landingPageId: uuid('landing_page_id'),

		// Cache identification
		cacheKey: text('cache_key').notNull().unique(),
		queryType: varchar('query_type', { length: 100 }).notNull(), // 'pageviews', 'visitors', 'timeseries', etc.

		// Cached data
		data: jsonb().notNull(),

		// Expiration
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),

		// Audit
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' })
			.defaultNow()
			.notNull()
			.$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'analytics_data_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'analytics_data_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.landingPageId],
			foreignColumns: [landingPages.landingPageId],
			name: 'analytics_data_landing_page_id_fk'
		}).onDelete('cascade'),
		index('analytics_data_cache_key_idx').using('btree', table.cacheKey.asc().nullsLast()),
		index('analytics_data_expires_at_idx').using('btree', table.expiresAt.asc().nullsLast()),
		index('analytics_data_org_event_landing_idx').using(
			'btree',
			table.organizationId.asc().nullsLast(),
			table.eventId.asc().nullsLast(),
			table.landingPageId.asc().nullsLast()
		)
	]
);

// Export Batches
export const exportBatches = pgTable(
	'export_batches',
	{
		exportBatchId: uuid('export_batch_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),

		// Batch metadata
		name: text('name'), // Optional user-friendly name like "Weekly Attendee Export"
		description: text('description'),

		// Status tracking
		status: exportBatchStatus().default('pending').notNull(),
		totalJobs: integer('total_jobs').default(0).notNull(),
		completedJobs: integer('completed_jobs').default(0).notNull(),
		failedJobs: integer('failed_jobs').default(0).notNull(),

		// Error handling
		errorMessage: text('error_message'),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		startedAt: timestamp('started_at', { mode: 'string' }),
		completedAt: timestamp('completed_at', { mode: 'string' }),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'export_batches_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'export_batches_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'export_batches_user_id_fk'
		}).onDelete('cascade'),
		index('export_batches_organization_id_idx').using('btree', table.organizationId.asc().nullsLast()),
		index('export_batches_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('export_batches_user_id_idx').using('btree', table.userId.asc().nullsLast()),
		index('export_batches_status_idx').using('btree', table.status.asc().nullsLast()),
		index('export_batches_created_at_idx').using('btree', table.createdAt.desc().nullsLast())
	]
);

// Export Jobs
export const exportJobs = pgTable(
	'export_jobs',
	{
		exportJobId: uuid('export_job_id').defaultRandom().primaryKey().notNull(),
		exportBatchId: uuid('export_batch_id').notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),

		// Job type and configuration
		type: exportJobType().notNull(),
		config: jsonb().notNull(), // { filters, selectedColumns } - schema varies by type

		// Execution order: jobs with same ordinal run in parallel, lower ordinals run first
		ordinal: integer('ordinal').default(0).notNull(),

		// Status tracking
		status: exportJobStatus().default('pending').notNull(),

		// Results
		fileName: text('file_name'),
		fileSize: integer('file_size'), // Size in bytes
		recordCount: integer('record_count'), // Number of records exported
		downloadUrl: text('download_url'),
		expiresAt: timestamp('expires_at', { mode: 'string' }),

		// Error handling
		errorMessage: text('error_message'),
		retryCount: integer('retry_count').default(0).notNull(),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		startedAt: timestamp('started_at', { mode: 'string' }),
		completedAt: timestamp('completed_at', { mode: 'string' }),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.exportBatchId],
			foreignColumns: [exportBatches.exportBatchId],
			name: 'export_jobs_batch_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'export_jobs_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'export_jobs_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'export_jobs_user_id_fk'
		}).onDelete('cascade'),
		index('export_jobs_batch_id_idx').using('btree', table.exportBatchId.asc().nullsLast()),
		index('export_jobs_organization_id_idx').using('btree', table.organizationId.asc().nullsLast()),
		index('export_jobs_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('export_jobs_user_id_idx').using('btree', table.userId.asc().nullsLast()),
		index('export_jobs_status_idx').using('btree', table.status.asc().nullsLast()),
		index('export_jobs_type_idx').using('btree', table.type.asc().nullsLast()),
		index('export_jobs_created_at_idx').using('btree', table.createdAt.desc().nullsLast()),
		// Composite index for finding next jobs to execute (batch + ordinal + status)
		index('export_jobs_batch_ordinal_status_idx').using(
			'btree',
			table.exportBatchId.asc().nullsLast(),
			table.ordinal.asc().nullsLast(),
			table.status.asc().nullsLast()
		)
	]
);

// Import Enums
export const importBatchStatus = pgEnum('import_batch_status', [
	'pending',
	'validating',
	'validated',
	'importing',
	'completed',
	'failed'
]);

export const importRowStatus = pgEnum('import_row_status', [
	'pending',
	'valid',
	'invalid',
	'skipped',
	'imported',
	'failed'
]);

export const importType = pgEnum('import_type', ['completed_waived', 'completed_payment_required', 'pending']);

// Import Batches
export const importBatches = pgTable(
	'import_batches',
	{
		importBatchId: uuid('import_batch_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),

		// File metadata
		originalFileName: text('original_file_name').notNull(),
		rowCount: integer('row_count').default(0).notNull(),
		validRowCount: integer('valid_row_count').default(0).notNull(),
		skippedRowCount: integer('skipped_row_count').default(0).notNull(),
		invalidRowCount: integer('invalid_row_count').default(0).notNull(),
		importedRowCount: integer('imported_row_count').default(0).notNull(),

		// Status tracking
		status: importBatchStatus().default('pending').notNull(),

		// Import configuration
		importType: importType('import_type'),
		columnMapping: jsonb('column_mapping'),
		sendConfirmationEmail: boolean('send_confirmation_email').default(false).notNull(),
		sendPaymentLinkEmail: boolean('send_payment_link_email').default(false).notNull(),
		sendInviteEmail: boolean('send_invite_email').default(false).notNull(),

		// Error handling
		errorMessage: text('error_message'),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		validatedAt: timestamp('validated_at', { mode: 'string' }),
		completedAt: timestamp('completed_at', { mode: 'string' }),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'import_batches_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'import_batches_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'import_batches_user_id_fk'
		}).onDelete('cascade'),
		index('import_batches_organization_id_idx').using('btree', table.organizationId.asc().nullsLast()),
		index('import_batches_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('import_batches_user_id_idx').using('btree', table.userId.asc().nullsLast()),
		index('import_batches_status_idx').using('btree', table.status.asc().nullsLast()),
		index('import_batches_created_at_idx').using('btree', table.createdAt.desc().nullsLast())
	]
);

// Import Rows (staging table for CSV rows)
export const importRows = pgTable(
	'import_rows',
	{
		importRowId: uuid('import_row_id').defaultRandom().primaryKey().notNull(),
		importBatchId: uuid('import_batch_id').notNull(),
		rowNumber: integer('row_number').notNull(),

		// Data
		rawData: jsonb('raw_data').notNull(),
		parsedData: jsonb('parsed_data'),

		// PR 2: per-row resolution payload (operation, matched attendee, diff,
		// errors, warnings). Populated by validate-batch. Consumed by
		// execute-import and the preview UI.
		resolution: jsonb('resolution'),

		// Status
		status: importRowStatus().default('pending').notNull(),
		validationErrors: jsonb('validation_errors'),
		isSelected: boolean('is_selected').default(true).notNull(),
		isDuplicate: boolean('is_duplicate').default(false).notNull(),

		// Result tracking
		attendeeId: uuid('attendee_id'),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.importBatchId],
			foreignColumns: [importBatches.importBatchId],
			name: 'import_rows_batch_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'import_rows_attendee_id_fk'
		}).onDelete('set null'),
		index('import_rows_batch_id_idx').using('btree', table.importBatchId.asc().nullsLast()),
		index('import_rows_status_idx').using('btree', table.status.asc().nullsLast()),
		index('import_rows_row_number_idx').using('btree', table.rowNumber.asc().nullsLast()),
		index('import_rows_batch_row_idx').using(
			'btree',
			table.importBatchId.asc().nullsLast(),
			table.rowNumber.asc().nullsLast()
		)
	]
);

export const stripeTestCalls = pgTable(
	'stripe_test_calls',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		runId: uuid('run_id').notNull(),
		orderId: uuid('order_id'),
		method: text('method').notNull(),
		request: jsonb('request').notNull(),
		response: jsonb('response').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('stripe_test_calls_run_id_idx').on(table.runId, table.createdAt)
	]
);

export const smokeTestPreviews = pgTable(
	'smoke_test_previews',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		runId: uuid('run_id').notNull(),
		orderId: uuid('order_id'),
		attendeeId: uuid('attendee_id'),
		stepIndex: integer('step_index').notNull(),
		proposedChanges: jsonb('proposed_changes').notNull(),
		previewResult: jsonb('preview_result').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('smoke_test_previews_run_id_idx').on(table.runId, table.createdAt)
	]
);

export const allocationReservations = pgTable(
	'allocation_reservations',
	{
		reservationId: uuid('reservation_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		entityType: varchar('entity_type', { length: 32 }).notNull(),
		entityId: uuid('entity_id').notNull(),
		quantity: integer('quantity').default(1).notNull(),
		expiresAt: timestamp('expires_at', { mode: 'string', withTimezone: true }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull()
			.$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'allocation_reservations_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'allocation_reservations_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'allocation_reservations_status_id_fk'
		}).onDelete('cascade'),
		unique('allocation_reservations_status_entity_unique').on(
			table.attendeeRegistrationStatusId,
			table.entityType,
			table.entityId
		),
		index('allocation_reservations_entity_active_idx').using(
			'btree',
			table.entityType.asc(),
			table.entityId.asc(),
			table.expiresAt.asc()
		),
		index('allocation_reservations_expires_at_idx').using('btree', table.expiresAt.asc()),
		pgPolicy('allocation_reservations_require_scope', {
			as: 'permissive',
			for: 'select',
			to: ['public'],
			using: sql`((organization_id = ANY ((regexp_split_to_array(current_setting('search.organization_ids'::text, true), ','::text))::text[])) OR (event_id = ANY ((regexp_split_to_array(current_setting('search.event_ids'::text, true), ','::text))::uuid[])))`
		})
	]
);
